// ─────────────────────────────────────────────────────────────────
// Glass-i — Inventory Dashboard (/erp/inventory)
//
// The Inventory landing page. Renders a composed, read-only snapshot
// from GET /api/erp/inventory/dashboard — EVERY figure is a live DB
// aggregate returned by the backend (inventoryDashboard.service). This
// page contains NO hard-coded sample numbers; empty sections (e.g. no
// off-cuts / no reorder levels set) render honest empty states.
// ─────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Row, Segmented, Spin, Statistic, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, DownloadOutlined, InboxOutlined, FileTextOutlined,
  CheckCircleOutlined, ArrowRightOutlined,
  AppstoreOutlined, DatabaseOutlined, WalletOutlined, WarningOutlined, TableOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import { inventoryApi } from './inventoryApi';
import './InventoryDashboardPage.css';

// ── formatting (Indian grouping; DB values only) ──────────────────
const NF0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
// CLAUDE.md rule 5A — glass area (SQM/SQFT) is ALWAYS shown at exactly 3 dp.
const NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
// Whole-number counts (sheets / pieces / lines).
function fmtInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? NF0.format(Math.round(n)) : '—';
}
// Area (m² / ft²) — strict 3-dp display; storage keeps full NUMERIC precision.
function fmtArea(v) {
  const n = Number(v);
  return Number.isFinite(n) ? NF3.format(n) : '—';
}
function fmtInrCompact(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${NF0.format(Math.round(n))}`;
}
function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
function fmtDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

const TYPE_COLORS = {
  GLASS: '#1877F2', HARDWARE: '#16A34A', ACCESSORY: '#7c3aed', PROFILE: '#D97706',
  UPVC: '#0d9488', ALUMINIUM: '#64748b', SERVICE: '#8b95a8', JOBWORK: '#8b95a8', OTHER: '#94a3b8'
};

function errMessage(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message)
    || (e && e.message) || fallback;
}

// ── one pivot cell (stock: sqmt/sheets · off-cut: sqmt/pieces) ────
function renderCell(cell, measure, kind, keyId, extraClass) {
  if (!cell) {
    return <td key={keyId} className="invd-empty">–</td>;
  }
  const count = kind === 'offcut' ? Number(cell.pieces || 0) : Number(cell.sheets || 0);
  const countUnit = kind === 'offcut' ? 'pc' : 'sh';
  const sqmt = Number(cell.sqmt || 0);
  const primary = measure === 'sheets' ? fmtInt(count) : fmtArea(sqmt);
  const sub = measure === 'sheets' ? `${fmtArea(sqmt)} m²` : `${fmtInt(count)} ${countUnit}`;
  return (
    <td key={keyId} className={`invd-val ${extraClass || ''}`}>
      <span className="invd-a">{primary}</span>
      <span className="invd-sub">{sub}</span>
    </td>
  );
}

function Pivot({ pivot, offcutPivot, measure }) {
  // Shared column set = union of both pivots' families (on-hand order first).
  const families = useMemo(() => {
    const map = new Map();
    (pivot.families || []).forEach((f) => map.set(f.code, f));
    (offcutPivot.families || []).forEach((f) => { if (!map.has(f.code)) map.set(f.code, f); });
    return [...map.values()];
  }, [pivot, offcutPivot]);

  const colSpanAll = families.length + 2;
  const hasOffcuts = (offcutPivot.rows || []).length > 0;

  return (
    <div className="invd-scroll">
      <table className="invd-pivot">
        <thead>
          <tr>
            <th className="invd-rl">Thickness</th>
            {families.map((f) => (
              <th key={f.code}>{f.label}</th>
            ))}
            <th className="invd-tot">Total</th>
          </tr>
        </thead>
        <tbody>
          {(pivot.rows || []).map((row, ri) => (
            <tr key={`on-${ri}`}>
              <td className="invd-rl">{row.thicknessLabel}<small> mm</small></td>
              {families.map((f) => renderCell(row.cells ? row.cells[f.code] : null, measure, 'stock', `on-${ri}-${f.code}`))}
              {renderCell(row.total, measure, 'stock', `on-${ri}-tot`, 'invd-tcol')}
            </tr>
          ))}

          {/* On-hand total */}
          <tr className="invd-ontot">
            <td className="invd-rl">On-hand Total</td>
            {families.map((f) => renderCell(pivot.colTotals ? pivot.colTotals[f.code] : null, measure, 'stock', `ont-${f.code}`))}
            {renderCell(pivot.grandTotal, measure, 'stock', 'ont-grand', 'invd-grand')}
          </tr>

          {/* Off-cut section — a separate register (m² · pieces), not on-hand stock */}
          <tr className="invd-sec">
            <th colSpan={colSpanAll}>
              Off-cuts — reusable remnants
              <small>recorded separately · not part of on-hand stock · m² · pieces</small>
            </th>
          </tr>

          {!hasOffcuts ? (
            <tr className="invd-ocempty">
              <td colSpan={colSpanAll}>No off-cuts recorded yet.</td>
            </tr>
          ) : (
            <>
              {(offcutPivot.rows || []).map((row, ri) => (
                <tr key={`oc-${ri}`} className="invd-oc">
                  <td className="invd-rl">{row.thicknessLabel}<small> mm</small></td>
                  {families.map((f) => renderCell(row.cells ? row.cells[f.code] : null, measure, 'offcut', `oc-${ri}-${f.code}`))}
                  {renderCell(row.total, measure, 'offcut', `oc-${ri}-tot`, 'invd-tcol')}
                </tr>
              ))}
              <tr className="invd-octot">
                <td className="invd-rl">Off-cut Total</td>
                {families.map((f) => renderCell(offcutPivot.colTotals ? offcutPivot.colTotals[f.code] : null, measure, 'offcut', `oct-${f.code}`))}
                {renderCell(offcutPivot.grandTotal, measure, 'offcut', 'oct-grand', 'invd-grand')}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function InventoryDashboardPage() {
  const { me } = useAuth();
  const navigate = useNavigate();

  const canView = useMemo(() => {
    const perms = Array.isArray(me && me.permissions) ? me.permissions : [];
    return perms.includes('*') || perms.includes('erp.inventory.stock.view');
  }, [me]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [measure, setMeasure] = useState('sqmt');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const out = await inventoryApi.getDashboard();
      setData(out || null);
    } catch (e) {
      message.error({ content: errMessage(e, 'Failed to load the inventory dashboard.'), key: 'invd-load' });
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <div className="invd-shell" style={{ padding: 16 }}>
        <Alert type="warning" showIcon
          message="No access"
          description="You do not have permission to view the inventory dashboard (erp.inventory.stock.view)." />
      </div>
    );
  }

  const k = (data && data.kpis) || {};
  const pipeline = (data && data.inwardPipeline) || { pendingGrns: 0, pendingBills: 0, postedSources: 0 };
  const mix = (data && data.mixByType) || [];
  const maxMix = mix.reduce((m, r) => Math.max(m, Number(r.rowCount || 0)), 0) || 1;
  const reorder = (data && data.reorderWatch && data.reorderWatch.rows) || [];
  const pulse = (data && data.movementPulse) || [];
  const idle = (data && data.idleStock) || { count: 0, days: 90 };
  const yieldOc = (data && data.offcutYield) || { pieces: 0, sqmt: 0 };

  return (
    <div className="invd-shell" style={{ padding: 16 }}>
      {/* header */}
      <div className="invd-head">
        <div>
          <div className="invd-head__eyebrow">Inventory</div>
          <h1 className="invd-head__title">Inventory Dashboard</h1>
          <p className="invd-head__sub">On-hand stock by thickness and category, and everything moving through the store.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div className="invd-head__aside">
            <div className="invd-head__live">Live · on-hand</div>
            {data && data.asOf ? <div>As on <b>{fmtDateTime(data.asOf)}</b></div> : null}
          </div>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {/* KPIs */}
        <div className="invd-kpis">
          <div className="invd-kpi acc-blue">
            <div className="invd-kpi__ic"><AppstoreOutlined /></div>
            <div className="invd-kpi__body">
              <div className="invd-kpi__lab">Glazed area on hand</div>
              <div className="invd-kpi__val">{fmtArea(k.glazedAreaSqmt)} <small>m²</small></div>
              <div className="invd-kpi__sub">{fmtArea(k.glazedAreaSqft)} ft² · {fmtInt(k.stockLines)} lines</div>
            </div>
          </div>
          <div className="invd-kpi acc-slate">
            <div className="invd-kpi__ic"><DatabaseOutlined /></div>
            <div className="invd-kpi__body">
              <div className="invd-kpi__lab">Sheets on hand</div>
              <div className="invd-kpi__val">{fmtInt(k.sheetsOnHand)}</div>
              <div className="invd-kpi__sub">{fmtInt(k.thicknessCount)} thickness · {fmtInt(k.categoryCount)} category · {fmtInt(k.sizeCount)} sizes</div>
            </div>
          </div>
          <div className="invd-kpi acc-green">
            <div className="invd-kpi__ic"><WalletOutlined /></div>
            <div className="invd-kpi__body">
              <div className="invd-kpi__lab">Reference value (posted)</div>
              <div className="invd-kpi__val">{fmtInrCompact(k.referenceValuePosted)}</div>
              <div className="invd-kpi__sub">from inward documents — not a cost valuation</div>
            </div>
          </div>
          <div className={`invd-kpi ${Number(k.reorderWatchCount) > 0 ? 'acc-red' : 'acc-amber'}`}>
            <div className="invd-kpi__ic"><WarningOutlined /></div>
            <div className="invd-kpi__body">
              <div className="invd-kpi__lab">Reorder watch</div>
              <div className="invd-kpi__val" style={{ color: Number(k.reorderWatchCount) > 0 ? '#DC2626' : undefined }}>
                {fmtInt(k.reorderWatchCount)}
              </div>
              <div className="invd-kpi__sub">items below reorder level</div>
            </div>
          </div>
        </div>

        {/* PIVOT */}
        <Card
          size="small"
          className="invd-pivot-card"
          style={{ marginBottom: 16 }}
          title={(
            <span className="invd-pivot-title">
              <span className="invd-pivot-title__ic"><TableOutlined /></span>
              Stock on hand
            </span>
          )}
          extra={
            <div className="invd-pivot-toolbar">
              <Segmented
                size="small"
                value={measure}
                onChange={setMeasure}
                options={[{ label: 'SQM (m²)', value: 'sqmt' }, { label: 'Sheets', value: 'sheets' }]}
              />
              <Tooltip title="Export coming soon">
                <Button size="small" icon={<DownloadOutlined />} disabled>Export CSV</Button>
              </Tooltip>
            </div>
          }
        >
          {data ? (
            <>
              <Pivot pivot={data.pivot} offcutPivot={data.offcutPivot} measure={measure} />
              <div className="invd-pivot-note">
                Blank (–) = that category not stocked in that thickness. Grouped by the glass family stored on each stock item.
              </div>
            </>
          ) : (
            <Empty description={loading ? ' ' : 'No stock data.'} />
          )}
        </Card>

        {/* CONTENT GRID */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            {/* Movement pulse */}
            <Card size="small" title="Movement pulse"
              extra={<a onClick={() => navigate('/erp/inventory/stock-ledger')}>Stock Ledger <ArrowRightOutlined /></a>}
              style={{ marginBottom: 16 }}>
              {pulse.length === 0 ? (
                <Empty description="No stock movements yet." />
              ) : (
                <ul className="invd-pulse">
                  {pulse.map((m, i) => {
                    const inward = m.movementType === 'PURCHASE_INWARD';
                    const reversal = m.movementType === 'PURCHASE_INWARD_REVERSAL';
                    const mkColor = inward ? '#16A34A' : reversal ? '#DC2626' : '#D97706';
                    const sign = inward ? '+' : reversal ? '−' : '';
                    return (
                      <li key={m.movementNo || i}>
                        <span className="invd-mk" style={{ background: mkColor }} />
                        <div>
                          <div className="invd-l1">
                            {m.itemName || '—'}
                            {m.thicknessMm != null ? <span className="invd-thk">{m.thicknessMm}mm</span> : null}
                          </div>
                          <div className="invd-l2">{m.movementNo} · {m.sourceType || '—'} · {fmtDate(m.movementDate)}</div>
                        </div>
                        <div className="invd-amt">
                          <div className="invd-q" style={{ color: mkColor }}>{sign}{fmtInt(m.qty)}</div>
                          <div className="invd-a">{fmtArea(m.sqmt)} m²</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            {/* Mix by item type */}
            <Card size="small" title="Mix by item type">
              {mix.length === 0 ? (
                <Empty description="No stock on hand." />
              ) : (
                <div className="invd-rows">
                  {mix.map((r) => (
                    <div className="invd-row" key={r.itemType}>
                      <div className="invd-row__l" style={{ flex: 1 }}>
                        <span className="invd-row__t" style={{ width: 92, flex: '0 0 auto' }}>{r.itemType}</span>
                        <span className="invd-mix-bar">
                          <span style={{
                            width: `${Math.max(4, (Number(r.rowCount || 0) / maxMix) * 100)}%`,
                            background: TYPE_COLORS[r.itemType] || '#94a3b8'
                          }} />
                        </span>
                      </div>
                      <span className="invd-row__v">{fmtInt(r.rowCount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            {/* Reorder watch */}
            <Card size="small" title="Reorder watch"
              extra={<a onClick={() => navigate('/erp/inventory/stock')}>Stock <ArrowRightOutlined /></a>}
              style={{ marginBottom: 16 }}>
              {reorder.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items below reorder level." />
              ) : (
                <div className="invd-rows">
                  {reorder.map((r, i) => (
                    <div className="invd-row" key={i}>
                      <div className="invd-row__l">
                        <div>
                          <div className="invd-row__t">{r.itemName || '—'}{r.thicknessMm != null ? ` ${r.thicknessMm}mm` : ''}</div>
                          <div className="invd-row__s">on-hand {fmtInt(r.onHand)} · reorder {fmtInt(r.reorderLevel)}</div>
                        </div>
                      </div>
                      <Tag color={r.status === 'OUT' ? 'red' : 'gold'}>
                        {r.status === 'OUT' ? 'OUT' : `−${fmtInt(r.deficit)}`}
                      </Tag>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Inward pipeline */}
            <Card size="small" title="Inward pipeline"
              extra={<a onClick={() => navigate('/erp/inventory/purchase-inward')}>Open <ArrowRightOutlined /></a>}
              style={{ marginBottom: 16 }}>
              <div className="invd-rows">
                <div className="invd-row">
                  <div className="invd-row__l"><InboxOutlined style={{ color: '#D97706' }} />
                    <div><div className="invd-row__t">GRNs pending posting</div><div className="invd-row__s">accepted, not yet in stock</div></div>
                  </div>
                  <span className="invd-row__v">{fmtInt(pipeline.pendingGrns)}</span>
                </div>
                <div className="invd-row">
                  <div className="invd-row__l"><FileTextOutlined style={{ color: '#1877F2' }} />
                    <div><div className="invd-row__t">Bills pending posting</div><div className="invd-row__s">approved purchase bills</div></div>
                  </div>
                  <span className="invd-row__v">{fmtInt(pipeline.pendingBills)}</span>
                </div>
                <div className="invd-row">
                  <div className="invd-row__l"><CheckCircleOutlined style={{ color: '#16A34A' }} />
                    <div><div className="invd-row__t">Posted sources</div><div className="invd-row__s">inward completed</div></div>
                  </div>
                  <span className="invd-row__v">{fmtInt(pipeline.postedSources)}</span>
                </div>
              </div>
            </Card>

            {/* Off-cut yield + Idle stock */}
            <Card size="small" title="Off-cut yield & idle stock"
              extra={<a onClick={() => navigate('/erp/inventory/offcuts')}>Off-cuts <ArrowRightOutlined /></a>}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Off-cut yield" value={fmtArea(yieldOc.sqmt)} suffix="m²" />
                  <div className="invd-muted" style={{ marginTop: 4 }}>{fmtInt(yieldOc.pieces)} pieces available</div>
                </Col>
                <Col span={12}>
                  <Statistic title={`Idle stock (${idle.days}d+)`} value={fmtInt(idle.count)} suffix="lines" />
                  <div className="invd-muted" style={{ marginTop: 4 }}>
                    {Number(idle.count) > 0 ? 'no movement in 90+ days' : 'all moved within 90 days'}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
}
